import { useState } from 'react';
import type { BloomRuntimeConfig } from '@apm/contracts';
import { GoogleButton } from '../auth/GoogleButton.js';
import { NativeSignInButton } from '../auth/NativeSignInButton.js';
import { googleRedirectOptions } from '../auth/callback.js';
import { loginMode, needsRedirectLogin, readDisplayFacts } from '../auth/login-mode.js';
import { takeLoginError } from '../auth/session.js';
import type { AuthApi } from '../auth/use-auth.js';
import { getNativeBridge } from '../native/bridge.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 로그인 화면.
 *
 * **가입 절차가 없다** (CLAUDE.md 머리 — "가입·로그인은 구글 계정으로만 하고 별도
 * 회원가입 절차가 없다"). 그래서 이 화면에 입력 칸이 하나도 없고, 버튼 하나와 그것이
 * 왜 필요한지를 말하는 문장뿐이다.
 *
 * ── 왜 로그인을 요구하는지 말한다 ─────────────────────────────
 *
 * 시청자에게 계정은 대가이고, 그 대가로 얻는 것이 이 확장이 증명하려는 것 자체다 —
 * **TV 에서 고른 상품이 손에 든 폰으로 온다**(Tier 1, `docs/12` §10). 그것을 적지 않으면
 * 로그인 화면은 이유 없는 관문으로 보인다.
 *
 * ── 로그인 없이 쓰는 배포 ─────────────────────────────────────
 *
 * `BLOOM_REQUIRE_LOGIN=false` 면 이 화면을 아예 지나지 않는다 (사업자 정책,
 * `docs/12` §2.4). 그 배포에서도 Tier 2·4 는 그대로 성립한다 — 계정은 가장 정확한
 * 첫 단계이지 유일한 단계가 아니다.
 */
export function LoginPage({ runtime, auth }: { runtime: BloomRuntimeConfig; auth: AuthApi }) {
  const t = useT();
  /*
   * **판정은 `loginMode` 하나다** (v17 R4). 설정 시트의 입구와 같은 함수를 본다 —
   * 나누면 「어느 입구로 들어왔느냐에 따라 로그인 뒤가 다르다」가 시작된다.
   *
   * 렌더마다 다시 읽는다. 셸 판정도 standalone 판정도 그 문서의 수명 동안 바뀌지 않고,
   * 붙들어 두면 로그인 리다이렉트로 문서가 갈릴 때 낡은 값이 남는다.
   */
  const mode = loginMode(
    runtime,
    auth.user,
    getNativeBridge(),
    needsRedirectLogin(readDisplayFacts()),
  );
  // redirect 콜백이 실패로 돌아왔는가 (v17 R9). **한 번만 읽는다** — 문서가 갈리는
  // 사이에 저장소로 넘어온 값이라 상태로는 올 수 없었다.
  const [callbackFailed] = useState(() => takeLoginError() !== null);

  return (
    <div className="card">
      <h2 className="card__title">{t('login.title')}</h2>
      <p className="card__text">{t('login.text')}</p>
      <p className="card__text">{t('login.benefit')}</p>

      <div style={{ marginTop: '1.1rem' }}>{entry()}</div>

      {callbackFailed && <p className="card__text">{t('login.callbackFailed')}</p>}
      {auth.error !== null && <p className="card__text">{auth.error}</p>}
    </div>
  );

  function entry() {
    switch (mode.kind) {
      case 'native':
        return (
          <NativeSignInButton
            signIn={mode.signIn}
            onIdToken={(idToken) => auth.loginWithGoogle(idToken)}
          />
        );

      /*
       * **셸인데 로그인 통로가 없다.** GIS 로 내려가지 않는다 — WKWebView 안에서는
       * 구글이 막아 「버튼이 조용히 안 뜬다」가 되고, 그것이 이 경로에서 가장 나쁜
       * 실패다 (`docs/29` D8). 할 일을 재설치 하나로 정해 준다.
       */
      case 'shellTooOld':
        return <p className="card__text">{t('login.shellTooOld')}</p>;

      /*
       * **로그인이 필요한데 클라이언트 ID 가 없는 배포다.** 서버가 부팅 시 경고를
       * 남기지만(`env.ts`) 그 로그는 시청자에게 보이지 않는다. 여기서 말하지 않으면
       * 화면에는 아무것도 없는 카드만 남는다 (`docs/11` §8 — "로그인 버튼이
       * 아예 안 보인다").
       */
      case 'notConfigured':
        return (
          <p className="card__text">
            {t('login.noClientId')}
            <code>GOOGLE_CLIENT_ID</code>).
          </p>
        );

      case 'none':
        return null;

      /*
       * `gisRedirect` 는 홈 화면 앱이다 — **팝업이 결과를 돌려주지 못하는 화면**
       * (`docs/40` D11). 그 갈래에서만 `redirect` 를 넘긴다: 구글이 우리 서버로
       * credential 을 POST 하고, 서버가 세션 토큰을 프래그먼트에 실어 앱으로 되돌린다.
       * **PC·Android 의 팝업은 건드리지 않는다** (D13).
       */
      case 'gis':
      case 'gisRedirect':
        return (
          <GoogleButton
            clientId={mode.clientId}
            onCredential={(idToken) => {
              void auth.loginWithGoogle(idToken);
            }}
            {...(mode.kind === 'gisRedirect'
              ? { redirect: googleRedirectOptions(window.location.origin) }
              : {})}
          />
        );
    }
  }
}
